import { motion } from 'framer-motion';

// A small icon badge with a genuine "line-drawing" ring animation: an SVG
// circle draws itself in (via framer-motion's normalized `pathLength`) as
// the badge scrolls into view, then the icon pops in with a spring. The
// LXRA variant additionally gets a slow, continuous pulse ring — a subtle
// looping motion graphic that visually differentiates the "AI-powered"
// column from the static "Traditional" one.
export const AnimatedIconBadge = ({ icon: Icon, variant = 'traditional', size = 44 }) => {
  const isLxra = variant === 'lxra';
  const color = isLxra ? 'hsl(var(--lxra-gold))' : 'hsl(var(--lxra-muted))';
  const ringColor = isLxra ? 'hsl(var(--lxra-gold) / 0.55)' : 'hsl(var(--lxra-border))';

  return (
    <div
      className="relative shrink-0"
      style={{ height: size, width: size }}
      data-testid={`how-it-works-icon-${variant}`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 48 48" className="absolute inset-0 h-full w-full -rotate-90">
        <circle cx="24" cy="24" r="21" fill="none" stroke={isLxra ? 'hsl(var(--lxra-border))' : 'transparent'} strokeWidth="1.5" />
        <motion.circle
          cx="24"
          cy="24"
          r="21"
          fill="none"
          stroke={ringColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        />
      </svg>

      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0, scale: 0.4, rotate: -12 }}
        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ delay: 0.25, type: 'spring', stiffness: 260, damping: 18 }}
      >
        <Icon className="h-[42%] w-[42%]" style={{ color }} strokeWidth={1.75} />
      </motion.div>

      {isLxra && (
        <motion.div
          className="absolute inset-0 rounded-full border"
          style={{ borderColor: 'hsl(var(--lxra-gold) / 0.4)' }}
          animate={{ scale: [1, 1.28, 1], opacity: [0.55, 0, 0.55] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
      )}
    </div>
  );
};
